import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/lis-logo.png';
import Icon, { IconName } from '../Icon/Icon';
import { Formprops } from './Form.types';
import './Form.css';

const descriptions: Record<string, string> = {
  'Iniciar sesión': 'Accede con las credenciales asociadas a tu cuenta institucional.',
  'Registrarse': 'Crea tu cuenta con la información registrada en la Universidad.',
  'Recuperar contraseña': 'Elige el método de recuperación que tengas disponible.',
  'Cambiar contraseña': 'Define una nueva contraseña para proteger tu cuenta.',
};

type AuthExperience = {
  kicker: string;
  heading: string;
  copy: string;
  highlights: { icon: IconName; title: string; text: string }[];
};

const experiences: Record<string, AuthExperience> = {
  'Iniciar sesión': {
    kicker: 'Bienvenido de nuevo',
    heading: 'Tu acceso a la comunidad LIS, en un solo lugar.',
    copy: 'Ingresa con tu cuenta para continuar usando los servicios del Laboratorio Integrado de Sistemas.',
    highlights: [
      { icon: 'graduation', title: 'Identidad universitaria', text: 'Usa las credenciales asociadas a tu cuenta institucional.' },
      { icon: 'users', title: 'Comunidad LIS', text: 'Un punto de acceso pensado para estudiantes y colaboradores.' },
    ],
  },
  'Registrarse': {
    kicker: 'Haz parte del LIS',
    heading: 'Crea una cuenta conectada con tu vida universitaria.',
    copy: 'Completa tus datos institucionales para empezar a usar los servicios actuales del laboratorio.',
    highlights: [
      { icon: 'check', title: 'Proceso guiado', text: 'Campos claros y validaciones visibles durante el registro.' },
      { icon: 'graduation', title: 'Datos institucionales', text: 'Ten a la mano tu usuario de MARES y documento.' },
    ],
  },
  'Recuperar contraseña': {
    kicker: 'Recupera tu acceso de forma segura',
    heading: 'Volver a tu cuenta debe ser un proceso claro.',
    copy: 'La recuperación utiliza exclusivamente tu correo institucional.',
    highlights: [
      { icon: 'check', title: 'Métodos protegidos', text: 'Verificación mediante un código enviado al correo.' },
      { icon: 'clock', title: 'En pocos pasos', text: 'Identifícate, verifica tu identidad y define una nueva contraseña.' },
    ],
  },
  'Cambiar contraseña': {
    kicker: 'Seguridad de tu cuenta',
    heading: 'Actualiza tu acceso con tranquilidad.',
    copy: 'Define una contraseña nueva y continúa usando los servicios actuales del laboratorio.',
    highlights: [
      { icon: 'check', title: 'Cambio directo', text: 'El proceso conserva la sesión y validación existentes.' },
      { icon: 'lightbulb', title: 'Recomendación', text: 'Utiliza una contraseña que no compartas con otros servicios.' },
    ],
  },
};

const Form = ({ form: { title, eyebrow = 'Cuenta LIS', description, onSubmit, backHref }, children }: Formprops) => {
  const experience = experiences[title] || experiences['Iniciar sesión'];

  return (
    <section className="auth-page">
      {backHref && (
        <Link to={backHref} className="auth-back-link">
          <Icon name="arrow-left" />
          Volver
        </Link>
      )}
      <div className="auth-ambient auth-ambient--one" aria-hidden="true" />
      <div className="auth-ambient auth-ambient--two" aria-hidden="true" />
      <div className="auth-layout lis-container">
        <aside className="auth-context" aria-label="Información de acceso al LIS">
          <div className="auth-context-inner">
            <div className="auth-brand">
              <span className="auth-brand-mark"><img src={logo} alt="" /></span>
              <span><strong>LIS</strong><small>Laboratorio Integrado de Sistemas</small></span>
            </div>
            <p className="auth-context-kicker">{experience.kicker}</p>
            <h2>{experience.heading}</h2>
            <p className="auth-context-copy">{experience.copy}</p>
            <div className="auth-highlights">
              {experience.highlights.map(({ icon, title: highlightTitle, text }) => (
                <div className="auth-highlight" key={highlightTitle}>
                  <span><Icon name={icon} /></span>
                  <div><strong>{highlightTitle}</strong><p>{text}</p></div>
                </div>
              ))}
            </div>
            <div className="auth-context-signal" aria-hidden="true">
              <span className="auth-signal-dot" />
              <span>Identidad LIS</span>
              <span className="auth-signal-line" />
            </div>
          </div>
        </aside>

        <div className="auth-form-column">
          <div className="form-container">
            <div className="form-header">
              <p className="lis-eyebrow">{eyebrow}</p>
              <h1 className="form-title">{title}</h1>
              <p className="form-description">{description || descriptions[title]}</p>
            </div>
            <form className="form" onSubmit={onSubmit} noValidate>
              <Fragment>{children}</Fragment>
            </form>
            <p className="form-support"><Icon name="mail" />¿Necesitas ayuda? <a href="mailto:laboratorio.lis@udea.edu.co">Escríbenos</a></p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;
